/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useGlobalState } from "../../utilities/hooks/use-global-state";
import { AnswerFormats } from "../../models/enumerations/answer-formats";
import { CategoryIds } from "../../models/enumerations/category-ids";
import { GroupIds } from "../../models/enumerations/group-ids";
import { SlideType } from "../../models/enumerations/slide-types";
import AssessmentSettings from "../../models/interfaces/assessment-settings";
import { Question } from "../../models/interfaces/questions/question";
import { LifestyleAssessmentResponse } from "../../models/interfaces/scores/lifestyle-assessment-response";
import { LifestyleScore } from "../../models/interfaces/scores/lifestyle-score";
import { MskScore } from "../../models/interfaces/scores/msk-score";
import { AssessmentUtils } from "../../utilities/assessments/assessment-utils";
import usePrevious from "../../utilities/hooks/use-previous";
import UserMskFocusArticlesUtil from "../../utilities/user-msk-focus-articles-util";
import AppModal from "../app-modal/app-modal";
import LoaderAssessments from "../loader-assessments/loader-assessments";
import ProgressBar from "../progress-bar/progress-bar";
import ScreenSlide from "../screen-slide/screen-slide";
import { useStoreAssessments } from "../../store/useStoreAssessments";
import { DateTime } from "luxon";
import UserService from "../../utilities/services/user-service";
import ReportService from "../../utilities/services/report-service-assessments/report-service-assessments";
import { ReportType } from "../../models/enumerations/report-type";
import { Report } from "../../models/interfaces/report";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { User } from "../../models/interfaces/user";
import BaseCardLayout from "../base-card-layout/base-card-layout";
import UserUtilAssessments from "../../utilities/user-util-assessments";
import LinkCreateAccountPage from "../../pages/link-create-account-page/link-create-account-page";
import { useTranslation } from 'react-i18next';
import { ActivityType } from "../../models/enumerations/activity-type";
import UserActivity from "../../models/interfaces/user-activity";
import DeeplinkService from "../../utilities/services/deeplink-service";
import { useHistory, useParams } from "react-router-dom";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import { UserInfo } from "../../models/interfaces/scores/user-info";
import { useNavigationDirection } from "../navigation-direction-context/navigation-direction-content";
import { scheduleAiMskScoreSummary } from "../../utilities/ai-response-utils";
import { createLogger } from "../../utilities/logger";

const log = createLogger("LinkLifestyleScreen");
const COMPONENT_CLASS = "c-movement-screen";

export interface LinkLifestyleScreenProps {
    screenSettings: AssessmentSettings;
    setScreenSettings: React.Dispatch<AssessmentSettings>;
    settingsOnBack?: Function;
    user: User;
    mskScore: MskScore;
    linkId: string;
}

interface LinkLifestyleScreenParams {
    id: string;
}

const LinkLifestyleScreen: React.FC<LinkLifestyleScreenProps> = (props) => {
    const { setDirection } = useNavigationDirection();
    const { id } = useParams<LinkLifestyleScreenParams>();
    const { screenSettings, setScreenSettings } = props;
    const { setGlobalError } = useGlobalState();
    const { state } = useAuthState();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userInfo, setUserInfo] = useState<UserInfo>();
    const history = useHistory();
    const [isLoadingNextGroup, setLoadingNextGroup] = useState(false);
    const [lifestyleScore, setLifestyleScore] = useState<
        LifestyleScore | undefined | null
    >(undefined);
    const [createWellnessProgram, setCreateWellnessProgram] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [errorIntro, setErrorIntro] = useState<string | undefined>();
    const [mskScore, setMskScore] = useState<MskScore>(props.mskScore);
    const [isCompleted, setIsCompleted] = useState(false);
    const previousGroupIndex = usePrevious<number>(
        screenSettings.currentGroupIndex
    );
    const retryMs: number[] = [500, 1000, 1500, 3000, 5000];
    const hasSubmitButtton =
        AssessmentUtils.isLastGroup(screenSettings) &&
        AssessmentUtils.isLastSlide(screenSettings);
    const { t } = useTranslation();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const groupId = params.get("groupId");
        UserUtilAssessments.getUserInfoForLinks(props.user).then((userInfo) => {
            if (!userInfo) {
                // they don't have bioSex or birthdate. Take them to setup screen
                history.replace("/setup");
            }
            setUserInfo(userInfo);
        });
        AssessmentUtils.fetchQuestions(
            screenSettings,
            CategoryIds.LIFESTYLE,
            groupId,
            props.linkId
        ).then((settings) => {
            settings.specificGroupId = groupId;
            setScreenSettings(settings);
            setIsLoading(false);
        }).catch((e) => {
            log.error("Failed to load lifestyle questions for link", {
                exception: e,
                linkId: props.linkId,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const shouldScore = AssessmentUtils.shouldScoreGroup(
            screenSettings,
            previousGroupIndex,
            screenSettings.currentGroupIndex
        );
        if (!shouldScore) {
            return;
        }

        setErrors([]);
        setIsLoading(true);
        scoreGroup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenSettings.currentGroupIndex]);

    useEffect(() => {
        if (hasSubmitButtton && !isLoading) {
            submit();
        }
    }, [hasSubmitButtton, isLoading])

    const scoreGroup = async (attemptIndex: number = 0): Promise<void> => {
        let assessmentResponse: LifestyleAssessmentResponse | undefined =
            undefined;
        try {
            if (!userInfo) {
                return;
            }
            assessmentResponse = await AssessmentUtils.scoreGroup(
                userInfo,
                screenSettings,
                previousGroupIndex,
                mskScore,
                props.linkId
            );
        } catch (err: any) {
            if (attemptIndex === 0) {
                setGlobalError("Lifestyle: Calculate", err);
            }
            if (attemptIndex < retryMs.length) {
                setTimeout(() => {
                    return scoreGroup(attemptIndex + 1);
                }, retryMs[attemptIndex]);
                return;
            } else {
                log.error("Failed to score lifestyle group after retries (link)", {
                    exception: err,
                    linkId: props.linkId,
                    groupId: previousGroupIndex,
                    attempts: attemptIndex + 1,
                });
                await handleError([
                    t('assessments.err_notScored'),
                ]);
                return;
            }
        }

        // error handling
        if (assessmentResponse == null) {
            log.error("Lifestyle group scoring returned no result (link)", {
                linkId: props.linkId,
                groupId: previousGroupIndex,
            });
            await handleError([
                t('assessments.err_notCalculated'),
            ]);
            return;
        }
        if (
            assessmentResponse.errors != null &&
            assessmentResponse.errors.length >= 1
        ) {
            log.error("Lifestyle group scored with errors (link)", {
                errors: assessmentResponse.errors,
                linkId: props.linkId,
                groupId: previousGroupIndex,
            });
            await handleError(
                assessmentResponse.errors,
                t('assessments.err_hasErrors')
            );
            return;
        }

        log.info("Lifestyle group scored for link user", {
            linkId: props.linkId,
            groupId: previousGroupIndex,
            mskScoreId: assessmentResponse.mskScore?.id,
        });

        if (assessmentResponse.mskScore) {
            setMskScore(assessmentResponse.mskScore);
        }
        setIsLoading(false);

    };

    const saveGroup = async (
        lifestyleAssessmentResponse: LifestyleAssessmentResponse,
        attemptIndex: number = 0
    ): Promise<void> => {
        let newMskScore: MskScore | undefined = undefined;
        try {
            newMskScore = await AssessmentUtils.saveGroupForLinks(
                lifestyleAssessmentResponse,
            );
        } catch (err: any) {
            if (attemptIndex === 0) {
                setGlobalError("Lifestyle: Save", err);
            }
            if (attemptIndex < retryMs.length) {
                setTimeout(() => {
                    return saveGroup(
                        lifestyleAssessmentResponse,
                        attemptIndex + 1
                    );
                }, retryMs[attemptIndex]);
                return;
            } else {
                await handleError([
                    t('assessments.err_couldNotBeSaved'),
                ]);
                return;
            }
        }
        // error handling
        if (newMskScore == null) {
            await handleError([
                t('assessments.err_notSaved'),
            ]);
            return;
        }
        if (newMskScore.errors != null && newMskScore.errors.length >= 1) {
            await handleError(
                newMskScore.errors,
                t('assessments.err_hasErrors2'),
            );
            return;
        }

        // handle changes
        if (!state.user || !state.user.id) {
            return
        }
        UserMskFocusArticlesUtil.handleFocusChanges(
            state.user.id,
            newMskScore
        );
        setMskScore(newMskScore);
        setIsLoading(false);
        setErrors([]);
    };

    // NOTE: this throws warnings in the tests for some reason, but works functionally
    const handleError = async (
        errors: string[],
        instructions?: string
    ): Promise<void> => {
        if (errors == null || errors.length === 0) {
            setErrors([]);
            return;
        }

        setScreenSettings(
            await AssessmentUtils.moveToPreviousPage(screenSettings)
        );
        setErrorIntro(instructions);
        setErrors(errors);
        setIsLoading(false);
    };

    const getToggleLoadingNextGroup = (settings: AssessmentSettings) => {
        let toggleLoadingNextGroup = false;
        const isLastSlideInGroup = AssessmentUtils.isLastSlide(settings);
        if (isLastSlideInGroup) {
            const group = settings.slideGroups[settings.currentGroupIndex];
            toggleLoadingNextGroup = group.nextGroupIdDeterminedByApi === true;
        }
        return toggleLoadingNextGroup;
    };

    const getNextPage = async () => {
        const toggleLoadingNextGroup =
            getToggleLoadingNextGroup(screenSettings);
        if (toggleLoadingNextGroup) {
            setLoadingNextGroup(true);
        }
        setDirection('forward');
        setScreenSettings(await AssessmentUtils.moveToNextPage(screenSettings, props.linkId));
        if (toggleLoadingNextGroup) {
            setLoadingNextGroup(false);
        }
    };

    const recordAnswer = async (selectedAnswer: string, question: Question, forceAutoSubmit?: boolean) => {
        let settings = AssessmentUtils.recordAnswer(
            screenSettings,
            question,
            selectedAnswer
        );

        if (
            slide?.slideType === SlideType.Question &&
            slide.questions != null
        ) {
            let autoSubmit = false;
            slide.questions.forEach((question) => {
                if (AssessmentUtils.autoSubmitQuestion(question)) {
                    autoSubmit = true;
                }
            });
            setDirection('forward');
            if (autoSubmit || forceAutoSubmit) {
                const toggleLoadingNextGroup =
                    getToggleLoadingNextGroup(settings);
                if (toggleLoadingNextGroup) {
                    setLoadingNextGroup(true);
                }
                settings = await AssessmentUtils.moveToNextPage(settings, props.linkId);
                if (toggleLoadingNextGroup) {
                    setLoadingNextGroup(false);
                }
            }
        }

        setScreenSettings(settings);
    };

    const submit = async () => {

        setIsLoading(true);
        setLifestyleScore(mskScore?.lifestyleScore);
        // already submitted
        setErrors(mskScore?.errors ?? []);
        if (mskScore?.errors && mskScore.errors.length) {
            setIsLoading(false);
        }

        const deepLink = await DeeplinkService.get(id).catch((e) => {
            log.error("Failed to fetch deeplink during link lifestyle submit", {
                exception: e,
                linkId: props.linkId,
            });
            return null;
        })
        if (props.user && props.user.id && deepLink?.linkType === SymmioAccessType.AppLicense && mskScore.movementScore?.percentage != null && mskScore.focusAreas != null &&
            mskScore.focusAreas.length >= 1) {
            setCreateWellnessProgram(true);
        }

        log.info("Link lifestyle assessment submitted", {
            linkId: props.linkId,
            mskScoreId: mskScore?.id,
            percentage: mskScore?.lifestyleScore?.percentage,
        });

        setIsCompleted(true);

    };

    const currentGroup =
        screenSettings?.slideGroups == null
            ? null
            : screenSettings.slideGroups[screenSettings.currentGroupIndex];
    const slide = AssessmentUtils.getSlide(screenSettings);
    const actionsModifier =
        slide?.slideType === SlideType.Video ? "-video" : "";

    let hasContinueButton =
        AssessmentUtils.hasData(screenSettings) &&
        (!AssessmentUtils.isLastGroup(screenSettings) ||
            !AssessmentUtils.isLastSlide(screenSettings));

    if (
        hasContinueButton &&
        slide &&
        slide?.slideType === SlideType.Question &&
        slide.questions != null
    ) {
        slide.questions.forEach((question) => {
            if (AssessmentUtils.autoSubmitQuestion(question)) {
                hasContinueButton = false;
            }
            if (AssessmentUtils.multiQuestion(question)) {
                hasContinueButton = false
            }
        });
    }

    let continueButtonText = t('buttons.btn_continue');;
    if (hasContinueButton) {
        switch (slide?.slideType) {
            case SlideType.AssessmentIntro:
            case SlideType.Tests:
                continueButtonText = t('buttons.btn_letsDoIt');
                break;
            case SlideType.GroupIntro:
                continueButtonText = t('buttons.btn_start');
                break;
            case SlideType.Video:
                const questions = AssessmentUtils.getSlide(
                    props.screenSettings
                )?.questions;
                if (
                    questions != null &&
                    questions.length >= 1 &&
                    questions[0].questionId === "BREATHING_BREATH"
                ) {
                    continueButtonText = t('buttons.btn_goToTimer');
                } else {
                    continueButtonText = t('buttons.btn_enterResults');
                }
                break;
        }
    }

    const noProgressBarSlideTypes = [
        SlideType.AssessmentIntro,
        SlideType.Complete,
        SlideType.GroupIntro,
        SlideType.Tests,
        SlideType.Tips,
    ];
    const hasProgressBar =
        slide == null ||
        noProgressBarSlideTypes.indexOf(slide.slideType) === -1;

    const collapseScrollTypes = [
        AnswerFormats.INTEGER,
        AnswerFormats.TIME,
        AnswerFormats.DECIMAL,
    ];
    const shouldCollapseScroll =
        slide?.slideType === SlideType.Question &&
        slide.questions &&
        slide.questions.filter(
            (q) => collapseScrollTypes.indexOf(q.answerFormat) > -1
        ).length > 0;

    const conditionalGroups = [
        GroupIds.BEHAVIORAL_QUESTIONNAIRE,
        GroupIds.SLEEP_PSQI,
        GroupIds.NUTRITION_QUESTIONNAIRE,
    ];
    const questionGroupNames = AssessmentUtils.getAssessmentGroups(
        screenSettings
    )
        .filter(
            (group) =>
                conditionalGroups.indexOf(
                    GroupIds[group.groupId as keyof typeof GroupIds]
                ) === -1
        )
        .map((group) => group.groupId!);

    // const handleGenerateReport = async (emailResults: boolean) => {
    //     if (!globalState.currentIdentity?.id) return;

    //     // we need to get the user from the firebase in case their organization
    //     // was switched (in the portal) and no longer matches the data in the app.
    //     const userInFirebase = await UserService.get(globalState.currentIdentity?.id);

    //     if (!userInFirebase || !userInFirebase.organizationId) return;

    //     const latestOrgId = userInFirebase.organizationId;

    //     //setIsLoading(true);

    //     const date = DateTime.now().toISODate();
    //     const report: Report = {
    //         dateEnd: date,
    //         dateStart: date,
    //         emailResults: emailResults,
    //         organizationId: latestOrgId,
    //         status: "scheduled",
    //         type: ReportType.Assessment,
    //         userId: globalState.currentIdentity!.id!,
    //     };

    //     const identity = globalState.currentIdentity;

    //     if (identity.organizationId !== latestOrgId) {
    //         identity.organizationId = latestOrgId;
    //     }

    //     const scheduledReport = await ReportService.save(
    //         report,
    //         identity
    //     );

    //     if (!scheduledReport.id) {
    //         // setIsLoading(false);
    //         // setReportError(
    //         //     "Report was not saved successfully. Please try again."
    //         // );
    //         return;
    //     }

    //     if (scheduledReport.emailResults === true) {
    //         // setShowReportEmailConfirmation(true);
    //         // setIsLoading(false);
    //         return;
    //     }

    //     // const reportUnsubscribe = ReportService.getSnapshot(
    //     //     scheduledReport.id,
    //     //     (r: Report) => {
    //     //         if (r.status === "complete" && r.url) {
    //     //             setIsLoading(false);
    //     //             reportUnsubscribe();

    //     //             //Loads in the System browser
    //     //             window.open(r.url, "_self");
    //     //         }
    //     //         if (r.status === "error") {
    //     //             setReportError(r.errorMessage);
    //     //             setIsLoading(false);
    //     //             reportUnsubscribe();
    //     //         }
    //     //     }
    //     // );
    // };

    return (
        <>
            {
                // if
                isCompleted && mskScore && (
                    <LinkCreateAccountPage
                        user={props.user}
                        mskScore={mskScore}
                        createWellnessProgram={createWellnessProgram}
                    />
                )
            }
            {!isCompleted &&
                <BaseCardLayout>
                    <div
                        className={`${COMPONENT_CLASS} -lifestyle ${shouldCollapseScroll ? "-collapse-scroll" : ""
                            }`}>
                        <LoaderAssessments
                            isLoading={isLoading || isLoadingNextGroup} />
                        {
                            // if
                            !isLoading && currentGroup != null && (
                                <div
                                    className={`${COMPONENT_CLASS}__wrapper ${slide?.slideType === SlideType.GroupIntro
                                        ? "-intro"
                                        : ""
                                        }`}>
                                    {
                                        // if
                                        AssessmentUtils.hasData(screenSettings) &&
                                        lifestyleScore == null && (
                                            <div
                                                className={`${COMPONENT_CLASS}__content ${hasContinueButton ||
                                                    hasSubmitButtton
                                                    ? "has-button"
                                                    : ""
                                                    }`}>
                                                <AppModal
                                                    isOpen={errors?.length >= 1}
                                                    onDismiss={() => {
                                                        setErrors([]);
                                                    }}>
                                                    <div
                                                        style={{
                                                            padding: "10px",
                                                            color: "#990000",
                                                        }}>
                                                        {
                                                            // if
                                                            errorIntro != null && (
                                                                <div>
                                                                    {errorIntro}
                                                                    <br />
                                                                    <br />
                                                                </div>
                                                            )
                                                        }
                                                        <div>{errors.join("; ")}</div>
                                                    </div>
                                                </AppModal>
                                                {
                                                    // if
                                                    slide != null && (
                                                        <ScreenSlide
                                                            questionGroupNames={
                                                                questionGroupNames
                                                            }
                                                            recordedAnswers={AssessmentUtils.getAllResponses(
                                                                screenSettings
                                                            )}
                                                            recordAnswer={recordAnswer}
                                                            page={slide}
                                                            errors={errors}
                                                            currentProgress={AssessmentUtils.getProcessCurrent(
                                                                screenSettings
                                                            )}
                                                            totalProgress={AssessmentUtils.getProcessTotal(
                                                                screenSettings
                                                            )}
                                                            handleOptionalQuestion={() =>
                                                                getNextPage()
                                                            }
                                                            settingsOnBack={props.settingsOnBack}
                                                            isActionsWithContinue={hasContinueButton}
                                                            skipButton={slide?.questions &&
                                                                slide.questions.length >
                                                                0 &&
                                                                slide.questions[0]
                                                                    .optional === true}
                                                            onSkip={getNextPage}
                                                            onBack={props.settingsOnBack}
                                                            onSubmit={getNextPage}
                                                            submitButtonText={continueButtonText}
                                                            hasProgressBar={hasProgressBar}
                                                            screenSettings={screenSettings}
                                                            headerText={t('assessments.lifestyle')}
                                                        />
                                                    )
                                                }
                                                <div
                                                    className={`${COMPONENT_CLASS}__actions ${actionsModifier}`}>
                                                    {/* {
                                        // if
                                        hasContinueButton && (
                                            <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={getNextPage} submitButtonText={continueButtonText} skipButton={slide?.questions &&
                                                slide.questions.length >
                                                0 &&
                                                slide.questions[0]
                                                    .optional === true}
                                                onSkip={() => {
                                                    getNextPage();
                                                }}></BaseCardLayoutActions>

                                        )
                                    } */}
                                                    {/* {
                                                // if
                                                hasSubmitButtton && (
                                                    <div className={`c-screen-slide__actions`}>
                                                        <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={submit} submitButtonText={errors.length === 0
                                                            ? "Submit Results"
                                                            : "Resubmit"}></BaseCardLayoutActions>
                                                    </div>
                                                    // <IonButton
                                                    //     color="primary"
                                                    //     expand="block"
                                                    //     onClick={() =>
                                                    //         submit()
                                                    //     }>
                                                    //     Submit Results
                                                    // </IonButton>
                                                )
                                            } */}

                                                    {/* {
                                        // if
                                        slide?.questions &&
                                        slide.questions.length >
                                        0 &&
                                        slide.questions[0]
                                            .optional === true && (
                                            <IonButton
                                                size="small"
                                                fill="clear"
                                                className={`${COMPONENT_CLASS}__optional-text`}
                                                onClick={() =>
                                                    getNextPage()
                                                }>
                                                Skip, I'll fill it
                                                in later.
                                            </IonButton>
                                        )
                                    } */}
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                        }
                    </div>
                </BaseCardLayout>
            }
        </>
    );
};

export default LinkLifestyleScreen;
